import colorPallete, { bodyFonts, elevation, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import Link from "@insite/mobius/Link";
import Img from "@insite/mobius/Img";
import CheckCircle from "@DSG/Icons/CheckCircle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import Hidden from "@insite/mobius/Hidden";

interface OwnProps extends WidgetProps {
   
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

export interface AddToCartModalStyles {
    container?: InjectableCss;
    itemAddedText?: TypographyProps;
    modalCloseWrapper?: InjectableCss;
    innerGridContainer?: GridContainerProps;
    leftGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    brandText?: TypographyProps;
    priceAndQtyValueText?: TypographyProps;
    productDescriptionText?: TypographyProps;
    imageWrapper?: InjectableCss;
}
 
export const addToCartModalStyles: AddToCartModalStyles = {
    container: { 
		css: css`
            color: ${colorPallete.grey800};
            position: fixed;
            width: 100%;
            z-index: 9999;
            height: 100%;
            top: 0;
            left: 0;
            right: 0;
            > div {
                border-bottom: 1px solid ${colorPallete.grey600DisabledText};
                background: ${colorPallete.white};
                margin-left: auto;
                width: 100%;
                padding: 8px 24px 24px;
                position: relative;
                box-shadow: ${elevation.dp04With25};
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        z-index: 9999;
                    `,
                ],
                "max",
            )}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        > div {
                            box-shadow: ${elevation.dp04};
                        }
                    `,
                    css`
                        margin: 0 auto;
                        background: ${colorPallete.white};
                        height: auto;
                        > div {
                            max-width: 1440px;
                            width: calc(100% - 80px);
                            margin: 0 auto;
                            padding: 16px 8px 16px 40px;
                        }
                            
                    `,
                ],
                "min",
            )}
		`
	},
    itemAddedText: {
        css: css`
            font: ${bodyFonts.body1SemiBoldLg.fontWeight} ${bodyFonts.body1SemiBoldLg.fontSize}/${bodyFonts.body1SemiBoldLg.lineHeight} ${bodyFonts.body1SemiBoldLg.fontFamily};
            display: flex;
            color: ${colorPallete.semanticGreen500};
            column-gap: 4px;
            align-items: center;
            svg {
                width: 24px;
                height: 24px;
                min-width: 24px;
            }
        `,
    },
    modalCloseWrapper: {
        css: css`
            position: absolute;
            color: ${colorPallete.grey800};
            cursor: pointer;
            top: 0;
            &.close-button {
                display: flex;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        position: absolute;
                        top: 8px;
                        right: 24px;
                    `,
                ],
                "max",
            )}
        `
    },
    innerGridContainer: {
        gap: 0,
        css: css`
            > div {
                gap: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        > [class*="GridOffset"] {
                            flex-wrap: nowrap;
                            gap: 24px;
                        }
                    `,
                ],
                "min",
            )}
        `
    },
    leftGridItem: {
        width: [12, 12, 5, 5, 5],
        css: css`
            > div {
                width: 100%;
            }
            .imageClickable {
                max-width: 103px;
                flex-basis: 103px;
                padding: 8px;
                justify-content: center;
                > span {
                    display: flex;
                }
                img {
                    max-height: 84px;
                    width: auto;
                    height: auto;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        max-width: 269px;
                        flex-basis: 269px;
                        min-width: 269px;
                    `,
                ],
                "min",
            )}
        `
    },
    rightGridItem: {
        width: [12, 12, 7, 7, 7],
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            > div {
                width: 100%;
                position: relative;
                .dsgBtn {
                    width: 100%;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        max-width: calc(100% - 269px);
                        flex-basis: calc(100% - 269px);
                        gap: 24px;
                        flex-direction: row;
                        > div {
                            &:first-child {
                                width: calc(100% - 160px);
                            }
                            &.buttonWrapper {
                                width: 160px;
                                padding-right: 8px;
                                display: flex;
                                gap: 24px;
                                align-items: center;
                                height: 100%;
                                .dsgBtn {
                                    padding-left: 12px;
                                    padding-right: 12px;
                                    width: auto;
                                    white-space: nowrap;
                                }
                            }
                        }   
                    `,
                ],
                "min",
            )}
        `
    },
    brandText: {
        css: css`
            font: ${labelFonts.label1SemiBold.fontWeight} ${labelFonts.label1SemiBold.fontSize}/${labelFonts.label1SemiBold.lineHeight} ${labelFonts.label1SemiBold.fontFamily};
            margin-bottom: 4px;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            overflow: hidden;
        `
    },
    productDescriptionText: {
        css: css`
            font: ${labelFonts.label1SemiBold.fontWeight} ${labelFonts.label1SemiBold.fontSize}/${labelFonts.label1SemiBold.lineHeight} ${labelFonts.label1SemiBold.fontFamily};
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            overflow: hidden;
            margin-bottom: 8px;
        `
    },
    imageWrapper: {
        css: css`
            display: flex;
            column-gap: 24px;
        `
    },
    priceAndQtyValueText: {
        css: css`
            font: ${labelFonts.label1SemiBold.fontWeight} ${labelFonts.label1SemiBold.fontSize}/${labelFonts.label1SemiBold.lineHeight} ${labelFonts.label1SemiBold.fontFamily};
            color: ${colorPallete.grey800};
            .strikeThroughPrice {
                font-weight: ${labelFonts.label1Regular.fontWeight};
                position: relative;
                &::after {
                    border-top: 1px solid ${colorPallete.grey800};
                    content: "";
                    height: 1px;
                    width: 100%;
                    position: absolute;
                    left: 0px;
                    top: 50%;
                }
            }
        `
    },
}

const styles = addToCartModalStyles;

const AddToCartModal: FC<Props> = ({
    cart,
    id,
    cartUrl,
}:Props) => {
    const [currentCartLine, setCurrentCartLine] = React.useState<CartLineModel | undefined>(undefined);
    React.useEffect(() => {
        if(typeof window !== "undefined") {
            if (window.sessionStorage.getItem("addToCartModalCartLine")) {
                const a = window.sessionStorage.getItem("addToCartModalCartLine");
                let b: any;
                if (a != null) {
                    b = JSON.parse(a);
                    setCurrentCartLine(b.cartLine);
                }
            } 
        }
    }); 
       
    const handleCloseAddToCartModal = () => {
        let addToCartModal : any;
        if(typeof window !== 'undefined') {
            addToCartModal = document.querySelector(".addToCartModal");
            if(addToCartModal) {
                addToCartModal.classList.remove('showAddToCartModal');
            }
            if(window.sessionStorage) {
                 window.sessionStorage.removeItem("addToCartModalCartLine");
            }
        } 
    };
    
    if(typeof window !== "undefined") {
        let addToCartModalWrapper = document.querySelector("#addToCartModalInnerWrapper");
        let addToCartModal = document.querySelector(".addToCartModal");
        document.addEventListener('click', function(event) {
            if(addToCartModalWrapper) {
                if (event.target instanceof Element && !event.target.closest('#addToCartModalInnerWrapper')) {
                    if(addToCartModal) {  
                        addToCartModal.classList.remove('showAddToCartModal');
                        if (window.sessionStorage) {
                            window.sessionStorage.removeItem("addToCartModalCartLine");
                        }
                    }
                }
            }
        });
    }

    if (typeof window !== 'undefined') {
        const carouselProducts = document.querySelectorAll('.carouselProduct a, .carouselProduct [data-test-selector="addToListLink"]');
        carouselProducts.forEach(carouselProduct => {
            carouselProduct.addEventListener('click', handleCloseAddToCartModal);
        });
    }

    if(!cart || !currentCartLine) {
        return null;
    }
    const cartCountText = translate("View cart") + " (" + cart.lineCount + ")";
    const pricingModel = currentCartLine.pricing;
    let hasPriceSavings = false;
    if (pricingModel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricingModel, currentCartLine.qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricingModel, currentCartLine.qtyOrdered || 1);

        hasPriceSavings = unitNetPrice < unitListPrice;
    }


    return (
        <> 
            <StyledWrapper {...styles.container} id="addToCartModalWrap">
                <StyledWrapper id="addToCartModalInnerWrapper">
                    <GridContainer {...styles.innerGridContainer}>
                        <GridItem {...styles.leftGridItem}>
                            <StyledWrapper {...styles.imageWrapper}>
                                <Typography as="p" {...styles.itemAddedText}>
                                    <CheckCircle/>
                                    {translate("Added to Cart")}
                                </Typography>
                                <Link href={currentCartLine?.productUri} className="imageClickable" onClick={handleCloseAddToCartModal}>
                                    <Img src={currentCartLine?.smallImagePath}/>
                                </Link>
                                <Hidden above="sm">
                                    <Typography {...styles.modalCloseWrapper} className="close-button" onClick={handleCloseAddToCartModal}>
                                        <X/>
                                        <VisuallyHidden>
                                            {translate("Close")}
                                        </VisuallyHidden>
                                    </Typography>
                                </Hidden>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.rightGridItem}>
                            <StyledWrapper>
                                <Hidden below="md">
                                    <Link className="text-link-primary" href={currentCartLine?.brand?.detailPagePath} {...styles.brandText}
                                        onClick={handleCloseAddToCartModal}>
                                        {currentCartLine?.brand?.name}
                                    </Link>
                                    <Link className="text-link-secondary" href={currentCartLine?.productUri} {...styles.productDescriptionText}
                                        onClick={handleCloseAddToCartModal}>
                                        {currentCartLine?.shortDescription}
                                    </Link>
                                </Hidden>
                                <Typography {...styles.priceAndQtyValueText}>
                                    {translate("Qty:")} {currentCartLine.qtyOrdered} {translate("for")} {currentCartLine.pricing?.extendedUnitNetPriceDisplay}{" "}
                                    {"("}{currentCartLine.pricing?.unitNetPriceDisplay}{"/"}{currentCartLine.pricing?.unitOfMeasure}
                                    {hasPriceSavings && (
                                        <>
                                            &nbsp;
                                            <Typography className="strikeThroughPrice">
                                                {currentCartLine.pricing?.regularPriceDisplay}{"/"}{currentCartLine.pricing?.unitOfMeasure} 
                                            </Typography>
                                        </>
                                    )}
                                    {")"}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper className="buttonWrapper">
                                <Link className="dsgBtn dsgPrimaryBtn" href={cartUrl} 
                                    onClick={handleCloseAddToCartModal}>
                                    {translate("Go to cart")}
                                </Link>
                                <Hidden below="md">
                                    <Typography  {...styles.modalCloseWrapper} className="close-button" onClick={handleCloseAddToCartModal}>
                                        <X/>
                                        <VisuallyHidden>
                                            {translate("Close")}
                                        </VisuallyHidden>
                                    </Typography>
                                </Hidden>
                                
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: (connect(mapStateToProps)(AddToCartModal)),
    definition: {
        group: "Common",
        icon: "link-simple",
    },
};

export default widgetModule;